import React, { useState } from "react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, Zap, Trophy } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const PLANS = [
  {
    key: "scout_pro_monthly",
    label: "Scout Pro",
    price: "$49",
    cadence: "/mo",
    icon: Zap,
    features: [
      "Unlimited player search",
      "Send trial invitations",
      "Shortlists & notes",
      "Basic filters",
    ],
  },
  {
    key: "club_elite_monthly",
    label: "Club Elite",
    price: "$199",
    cadence: "/mo",
    icon: Trophy,
    highlight: true,
    features: [
      "Everything in Scout Pro",
      "Advanced physical-stat filters",
      "Verified-first priority queue",
      "Relocation case management",
      "Priority support",
    ],
  },
];

export default function Billing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState("");

  const checkout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setLoading(""); }
  };

  const tier = user?.subscription_tier;

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">Pricing</div>
        <h1 className="mt-2 font-display text-5xl font-bold uppercase tracking-tight">Unlock the pipeline</h1>
        <p className="mt-4 text-white/70 max-w-xl">Clubs and scouts subscribe to access verified African talent. Cancel anytime.</p>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const active = tier === (p.key === "scout_pro_monthly" ? "scout_pro" : "club_elite");
            return (
              <div
                key={p.key}
                data-testid={`plan-${p.key}`}
                className={`border p-8 relative ${p.highlight ? "border-[#e65f2b] bg-[#e65f2b]/5" : "border-white/10 bg-[#141414]"}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-6 bg-[#eab308] text-black px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Most popular</div>
                )}
                <Icon className={p.highlight ? "text-[#e65f2b]" : "text-[#eab308]"} size={32} />
                <div className="mt-4 font-display text-3xl font-bold uppercase">{p.label}</div>
                <div className="mt-2">
                  <span className="font-display text-5xl font-bold">{p.price}</span>
                  <span className="text-white/50 text-sm">{p.cadence}</span>
                </div>
                <ul className="mt-6 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-[#2A9D8F] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  disabled={loading === p.key || active}
                  onClick={() => checkout(p.key)}
                  data-testid={`checkout-${p.key}`}
                  className={`mt-8 w-full h-11 rounded-sm uppercase tracking-widest font-bold ${
                    p.highlight ? "bg-[#e65f2b] hover:bg-[#c54a1c] text-black" : "bg-white text-black hover:bg-white/90"
                  }`}
                >
                  {active ? "Current plan" : loading === p.key ? "Redirecting…" : "Subscribe"}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-xs text-white/40">Test card: 4242 4242 4242 4242 · any future expiry · any CVC.</p>
      </div>
    </div>
  );
}
